import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button, CircularProgress, Autocomplete, TextField, Chip, IconButton } from "@mui/material";
import { FiArrowLeft, FiUpload, FiTrash2, FiDownload, FiPaperclip, FiZap, FiAlertCircle, FiCheckCircle, FiRefreshCw, FiFileText, FiUser, FiClock, FiTag, FiSend } from "react-icons/fi";
import API_BASE_URL from "./RestAPI";
import { showSuccessToast, showErrorToast } from "../constants/ToastService";
import ArtifactsView from "./RequirementTickets/components/ArtifactsView";
import OpenQuestionsForm from "./RequirementTickets/components/OpenQuestionsForm";
import "./RequirementTickets/RequirementTickets.css";

const ClientRequirementDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [requirement, setRequirement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [impactAreas, setImpactAreas] = useState([]);
    const [impactAreaOptions, setImpactAreaOptions] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [sprints, setSprints] = useState([]);
    const [selectedSprintId, setSelectedSprintId] = useState('');
    const [selectedTicketType, setSelectedTicketType] = useState('Story');
    const [selectedPriorityId, setSelectedPriorityId] = useState('');
    const [priorityTypes, setPriorityTypes] = useState([]);
    const [movingToSprint, setMovingToSprint] = useState(false);

    const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    };

    const loadRequirement = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/detail/?id=${id}`, { headers });
            const data = await res.json();
            if (data.status === "success") {
                setRequirement(data.data);
                setImpactAreas(data.data.impact_areas || []);
            } else showErrorToast(data.message || "Failed to load requirement");
        } catch (e) { showErrorToast("Failed to load requirement"); }
        finally { setLoading(false); }
    }, [id]);

    const loadImpactAreas = useCallback(async (projectId) => {
        try {
            const url = projectId
                ? `${API_BASE_URL}client_requirements/impact-areas/?project_id=${projectId}`
                : `${API_BASE_URL}client_requirements/impact-areas/`;
            const res = await fetch(url, { headers });
            const data = await res.json();
            if (data.status === "success") setImpactAreaOptions(data.data.filter(a => a.is_active).map(a => a.name));
        } catch (e) { console.error("Failed to load impact areas"); }
    }, []);

    const loadSprints = useCallback(async (projectId) => {
        if (!projectId) return;
        try {
            const res = await fetch(`${API_BASE_URL}ticketing/sprint/?project_id=${projectId}`, { headers });
            const data = await res.json();
            if (data.status === "success" && Array.isArray(data.data)) {
                setSprints(data.data.sort((a, b) => new Date(b.end_date) - new Date(a.end_date)));
            }
        } catch (e) { console.error(e); }
    }, []);

    const loadPriorityTypes = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}core/priorityType/`, { headers });
            const data = await res.json();
            if (data.status === "success") setPriorityTypes(data.data || []);
        } catch (e) { console.error(e); }
    }, []);

    const handleMoveToSprint = async () => {
        if (!selectedSprintId) { showErrorToast("Please select a sprint"); return; }
        if (!selectedPriorityId) { showErrorToast("Please select a priority"); return; }
        setMovingToSprint(true);
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/`, {
                method: "POST", headers,
                body: JSON.stringify({
                    ...requirement,
                    sprint_id: selectedSprintId,
                    ticket_type: selectedTicketType,
                    priority_id: selectedPriorityId,
                    id: requirement.id,
                }),
            });
            const data = await res.json();
            if (data.status === "success") {
                showSuccessToast("Requirement moved to sprint!");
                await loadRequirement();
                setSelectedSprintId('');
                setSelectedTicketType('Story');
                setSelectedPriorityId('');
            } else showErrorToast(data.message || "Failed to move");
        } catch (e) { showErrorToast("Failed to move to sprint"); }
        finally { setMovingToSprint(false); }
    };

    useEffect(() => { loadRequirement(); }, [loadRequirement]);
    useEffect(() => {
        if (requirement?.project_id_annotated) {
            loadImpactAreas(requirement.project_id_annotated);
            loadSprints(requirement.project_id_annotated);
            loadPriorityTypes();
        } else {
            loadImpactAreas('');
        }
    }, [requirement?.project_id_annotated]);

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/generate/`, { method: "POST", headers, body: JSON.stringify({ id, impact_areas: impactAreas }) });
            const data = await res.json();
            if (data.status === "success") showSuccessToast("Artifacts generated successfully");
            else showErrorToast(data.message || "Generation failed");
        } catch (e) { showErrorToast("Generation failed"); }
        finally { setGenerating(false); await loadRequirement(); }
    };

    const handleRegenerate = async () => {
        setRegenerating(true);
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/regenerate/`, { method: "POST", headers, body: JSON.stringify({ id }) });
            const data = await res.json();
            if (data.status === "success") showSuccessToast("Artifacts regenerated successfully");
            else showErrorToast(data.message || "Regeneration failed");
        } catch (e) { showErrorToast("Regeneration failed"); }
        finally { setRegenerating(false); await loadRequirement(); }
    };

    const handleSaveAnswers = async (answers) => {
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/answer/`, { method: "POST", headers, body: JSON.stringify({ id, answers }) });
            const data = await res.json();
            if (data.status === "success") { showSuccessToast("Answers saved"); await loadRequirement(); }
            else showErrorToast(data.message || "Failed to save answers");
        } catch (e) { showErrorToast("Failed to save answers"); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("requirement_id", id);
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/attachments/`, {
                method: "POST", headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }, body: formData,
            });
            const data = await res.json();
            if (data.status === "success") { showSuccessToast("File uploaded"); await loadRequirement(); }
            else showErrorToast(data.message || "Upload failed");
        } catch (err) { showErrorToast("Upload failed"); }
        finally { setUploading(false); e.target.value = ""; }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        try {
            const res = await fetch(`${API_BASE_URL}client_requirements/requirements/attachments/?id=${attachmentId}`, { method: "DELETE", headers });
            const data = await res.json();
            if (data.status === "success") { showSuccessToast("Attachment removed"); await loadRequirement(); }
            else showErrorToast(data.message || "Failed to remove");
        } catch (e) { showErrorToast("Failed to remove attachment"); }
    };

    if (loading) return <div className="crd-page"><div className="crd-loading"><CircularProgress size={28} sx={{ color: 'var(--accent)' }} /><span>Loading...</span></div></div>;
    if (!requirement) return <div className="crd-page"><div className="crd-loading"><p>Requirement not found.</p><button className="crd-back-btn" onClick={() => navigate("/ClientRequirements")}>← Back</button></div></div>;

    const artifact = requirement.artifact;
    const genStatus = requirement.generation_status || "NOT_STARTED";
    const isDone = genStatus === "DONE";
    const isFailed = genStatus === "FAILED";
    const inProgress = generating || regenerating || genStatus === "IN_PROGRESS";
    const attachments = requirement.attachments || [];

    const ticketLike = { id: requirement.id, title: requirement.requirement, requirement_text: requirement.description, impact_areas: requirement.impact_areas || [], generation_status: genStatus, generation_error: requirement.generation_error, open_question_answers: requirement.open_question_answers || {}, artifact };

    const TABS = [
        { key: 'overview', label: 'Overview' },
        { key: 'attachments', label: 'Attachments', count: attachments.length },
        { key: 'artifacts', label: 'Artifacts', show: !!artifact },
    ].filter(t => t.show !== false);

    return (
        <div className="crd-page">
            {/* ── Top Bar ── */}
            <div className="crd-topbar">
                <button className="crd-back-btn" onClick={() => navigate("/ClientRequirements")}>←</button>
                <span className="crd-topbar__breadcrumb">Client Requirements</span>
                <span className="crd-topbar__sep">/</span>
                <span className="crd-topbar__code">{requirement.requirement_code || `#${requirement.id}`}</span>
            </div>

            {/* ── Hero Card ── */}
            <div className="crd-hero">
                <div className="crd-hero__main">
                    <h1 className="crd-hero__title">{requirement.requirement_code} — {requirement.client_name}</h1>
                    <div className="crd-hero__badges">
                        <span className={`crd-status-pill ${/r&d|review|progress/i.test(requirement.status_name) ? 'crd-status-pill--blue' : /done|completed|approved/i.test(requirement.status_name) ? 'crd-status-pill--green' : /reject|fail|cancel/i.test(requirement.status_name) ? 'crd-status-pill--red' : 'crd-status-pill--gray'}`}>{requirement.status_name || '—'}</span>
                        <span className="crd-meta-chip"><FiTag size={11} /> {requirement.priority || '—'}</span>
                        {requirement.sprint_name && <span className="crd-meta-chip crd-meta-chip--accent">{requirement.sprint_name}</span>}
                        {requirement.origin === 'SPRINT' && <span className="crd-meta-chip" title="This requirement originated from a sprint ticket"><FiClock size={11} /> From Sprint</span>}
                    </div>
                    <div className="crd-hero__info-grid">
                        <div className="crd-info-item"><FiUser size={12} /><span className="crd-info-label">Assigned</span><span>{requirement.assigned_to_name || '—'}</span></div>
                        <div className="crd-info-item"><FiFileText size={12} /><span className="crd-info-label">Client</span><span>{requirement.client_name || '—'}</span></div>
                        {requirement.project_name && <div className="crd-info-item"><FiTag size={12} /><span className="crd-info-label">Project</span><span>{requirement.project_name}</span></div>}
                    </div>
                </div>

                {/* Generation Status Indicator */}
                <div className={`crd-gen-status ${isDone ? 'crd-gen-status--done' : isFailed ? 'crd-gen-status--fail' : inProgress ? 'crd-gen-status--progress' : ''}`}>
                    {isDone && <><FiCheckCircle size={16} /><div><strong>Artifacts Ready</strong><span>Can be moved to sprint</span></div></>}
                    {isFailed && <><FiAlertCircle size={16} /><div><strong>Generation Failed</strong><span>{requirement.generation_error || 'Unknown error'}</span></div></>}
                    {!isDone && !isFailed && !inProgress && <><FiZap size={16} /><div><strong>Not Generated</strong><span>Generate artifacts to proceed</span></div></>}
                    {inProgress && <><CircularProgress size={16} sx={{ color: 'var(--accent)' }} /><div><strong>Generating...</strong><span>Please wait</span></div></>}
                </div>
            </div>

            {/* ── Tabs ── */}
            <div className="crd-tabs">
                {TABS.map(tab => (
                    <button key={tab.key} className={`crd-tab ${activeTab === tab.key ? 'crd-tab--active' : ''}`} onClick={() => setActiveTab(tab.key)}>
                        {tab.label}
                        {tab.count > 0 && <span className="crd-tab__badge">{tab.count}</span>}
                    </button>
                ))}
            </div>

            {/* ── Tab Content ── */}
            <div className="crd-content">
                {activeTab === 'overview' && (
                    <>
                        {/* Requirement */}
                        <div className="crd-card">
                            <h3 className="crd-card__title">Requirement</h3>
                            <p className="crd-card__text">{requirement.requirement || '—'}</p>
                        </div>

                        {/* Description */}
                        {requirement.description && (
                            <div className="crd-card">
                                <h3 className="crd-card__title">Description</h3>
                                <p className="crd-card__text">{requirement.description}</p>
                            </div>
                        )}

                        {/* User Story & Criteria */}
                        {(requirement.user_story || requirement.acceptance_criteria) && (
                            <div className="crd-card-row">
                                {requirement.user_story && (
                                    <div className="crd-card crd-card--half">
                                        <h3 className="crd-card__title">User Story</h3>
                                        <p className="crd-card__text">{requirement.user_story}</p>
                                    </div>
                                )}
                                {requirement.acceptance_criteria && (
                                    <div className="crd-card crd-card--half">
                                        <h3 className="crd-card__title">Acceptance Criteria</h3>
                                        <p className="crd-card__text">{requirement.acceptance_criteria}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Artifact Generation Section */}
                        <div className="crd-card">
                            <h3 className="crd-card__title">Artifact Generation</h3>
                            {!isDone && (
                                <div style={{ marginBottom: 14 }}>
                                    <Autocomplete multiple options={impactAreaOptions} value={impactAreas} onChange={(_, v) => setImpactAreas(v)}
                                        renderInput={params => <TextField {...params} label="Impact areas" placeholder="Select or type impact areas" size="small" />}
                                        renderTags={(value, getTagProps) => value.map((option, index) => <Chip key={option} label={option} {...getTagProps({ index })} size="small" />)}
                                        sx={{ marginBottom: 1 }}
                                    />
                                </div>
                            )}
                            <div className="crd-gen-actions">
                                {(!isDone || isFailed) && (
                                    <button className="crd-gen-btn crd-gen-btn--primary" onClick={handleGenerate} disabled={inProgress}>
                                        {inProgress && !regenerating ? <><CircularProgress size={14} sx={{ color: '#fff' }} /> Generating...</> : isFailed ? <><FiRefreshCw size={13} /> Retry Generate</> : <><FiZap size={13} /> Generate Artifacts</>}
                                    </button>
                                )}
                                {isDone && (
                                    <button className="crd-gen-btn crd-gen-btn--outline" onClick={handleRegenerate} disabled={inProgress}>
                                        {regenerating ? <><CircularProgress size={14} sx={{ color: 'var(--accent)' }} /> Regenerating...</> : <><FiRefreshCw size={13} /> Regenerate</>}
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Move to Sprint — artifact generation is optional */}
                        {!requirement.sprint_name && !requirement.is_moved_to_sprint && (
                            <div className="crd-card crd-card--sprint">
                                <div className="crd-sprint-header">
                                    <div>
                                        <h3 className="crd-card__title"><FiSend size={13} /> Move to Sprint</h3>
                                        <p className="crd-sprint-desc">{isDone ? 'Artifacts are ready. Select a sprint to move this requirement into development.' : 'Select a sprint to move this requirement. Generating artifacts first is optional.'}</p>
                                    </div>
                                </div>
                                <div className="crd-sprint-select-row">
                                    <select className="crd-sprint-select" value={selectedSprintId} onChange={e => setSelectedSprintId(e.target.value)}>
                                        <option value="" disabled>Select a sprint...</option>
                                        {sprints.map(s => <option key={s.id} value={s.id}>{s.sprint_name} ({s.start_date} — {s.end_date})</option>)}
                                    </select>
                                    <select className="crd-sprint-select" style={{ maxWidth: 120 }} value={selectedTicketType} onChange={e => setSelectedTicketType(e.target.value)} title="Point Type">
                                        <option value="Story">Story</option>
                                        <option value="Epic">Epic</option>
                                    </select>
                                    <select className="crd-sprint-select" style={{ maxWidth: 140 }} value={selectedPriorityId} onChange={e => setSelectedPriorityId(e.target.value)} title="Priority">
                                        <option value="" disabled>Priority...</option>
                                        {priorityTypes.map(p => <option key={p.id} value={p.id}>{p.priority}</option>)}
                                    </select>
                                    <button className="crd-sprint-move-btn" onClick={handleMoveToSprint} disabled={!selectedSprintId || !selectedPriorityId || movingToSprint}>
                                        {movingToSprint ? <><CircularProgress size={14} sx={{ color: '#fff' }} /> Moving...</> : <><FiSend size={13} /> Move to Sprint</>}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Already moved indicator */}
                        {requirement.sprint_name && (
                            <div className="crd-card crd-card--moved">
                                <FiCheckCircle size={15} />
                                <div>
                                    <strong>Moved to Sprint</strong>
                                    <span>{requirement.sprint_name}</span>
                                </div>
                            </div>
                        )}

                        {/* Movement Timeline */}
                        {(requirement.activity_logs || []).length > 0 && (
                            <div className="crd-card">
                                <h3 className="crd-card__title"><FiClock size={13} /> Movement History</h3>
                                <div className="crd-att-list">
                                    {(requirement.activity_logs || []).map(log => (
                                        <div key={log.id} className="crd-att-item" style={{ alignItems: 'flex-start' }}>
                                            <FiClock size={13} className="crd-att-item__icon" style={{ marginTop: 2 }} />
                                            <div style={{ flex: 1 }}>
                                                <div>
                                                    <strong style={{ fontSize: '0.78rem' }}>
                                                        {log.event_type === 'MOVED_TO_SPRINT' ? 'Moved to Sprint'
                                                            : log.event_type === 'REMOVED_TO_SPRINT' ? 'Re-moved to Sprint'
                                                            : log.event_type === 'MOVED_BACK' ? 'Moved back to Requirements'
                                                            : log.event_type === 'CREATED_FROM_SPRINT' ? 'Created from Sprint ticket'
                                                            : log.event_type === 'SPRINT_CHANGED' ? 'Sprint changed'
                                                            : log.event_type === 'STATUS_SYNCED' ? 'Status synced'
                                                            : log.event_type}
                                                    </strong>
                                                    {log.sprint_name_annotated && <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}> — {log.sprint_name_annotated}</span>}
                                                    {log.ticket_code && <span style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}> ({log.ticket_code})</span>}
                                                </div>
                                                {log.note && <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem' }}>{log.note}</div>}
                                                <div style={{ color: 'var(--text-subtle)', fontSize: '0.68rem' }}>
                                                    {log.changed_by || '—'} · {log.event_date ? new Date(log.event_date).toLocaleString() : ''}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Open Questions */}
                        {artifact && <OpenQuestionsForm ticket={ticketLike} artifact={artifact} onSaveAnswers={handleSaveAnswers} />}
                    </>
                )}

                {activeTab === 'attachments' && (
                    <div className="crd-card">
                        <div className="crd-card__header-row">
                            <h3 className="crd-card__title">Attachments</h3>
                            <label className="crd-upload-btn">
                                <input type="file" style={{ display: 'none' }} onChange={handleFileUpload} disabled={uploading} />
                                {uploading ? <CircularProgress size={14} sx={{ color: 'var(--accent)' }} /> : <FiUpload size={13} />}
                                {uploading ? 'Uploading...' : 'Upload File'}
                            </label>
                        </div>
                        {attachments.length > 0 ? (
                            <div className="crd-att-list">
                                {attachments.map(att => (
                                    <div key={att.id} className="crd-att-item">
                                        <FiPaperclip size={13} className="crd-att-item__icon" />
                                        <a href={att.attachment_key} target="_blank" rel="noopener noreferrer" className="crd-att-item__name">{att.filename}</a>
                                        <button className="crd-att-item__del" onClick={() => handleDeleteAttachment(att.id)} title="Remove"><FiTrash2 size={12} /></button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="crd-empty-text">No attachments uploaded yet.</p>
                        )}
                    </div>
                )}

                {activeTab === 'artifacts' && artifact && (
                    <ArtifactsView artifact={artifact} />
                )}
            </div>
        </div>
    );
};

export default ClientRequirementDetail;
